import Styles from './Topics.module.scss'
import { FormikArrayTextAreaInput } from 'components/FirstTimeExperienceModal/steps/BrandProfile/FormikArrayTextAreaInput'
import { startCase } from 'lodash'
import { useCallback, useState } from 'react'
import { FieldArray, useField } from 'formik'
import { Button } from 'blaze-ui/Button'
import { Plus, ArrowRefresh } from 'blaze-ui/icons'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { mergeProps } from '@react-aria/utils'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { TopicEditDrawer } from './TopicEditDrawer'

interface Props {
  name: string
  title?: string
  icon?: React.ReactNode
  minValue?: number
  description?: string
  isRegenerating?: boolean
  onRegenerate?: () => void
}

interface AddTopicButtonProps extends ButtonProps {
  name: string
}

const AddTopicButton = ({
  name,
  variant = 'tertiary',
  size = 'md',
  frontIcon = Plus,
  ...rest
}: AddTopicButtonProps) => {
  const [field] = useField<string[]>(name)

  return (
    <FieldArray name={name}>
      {({ push }) => (
        <div className={Styles.addTopicButtonContainer}>
          <Button
            variant={variant}
            size={size}
            frontIcon={Plus}
            isDisabled={field.value.some((value) => value.trim().length === 0)}
            aria-label="Add topic"
            {...mergeProps({ onPress: () => push('') }, rest)}
          >
            Add Topic
          </Button>
        </div>
      )}
    </FieldArray>
  )
}

interface RegenerateButtonProps {
  isRegenerating?: boolean
  onRegenerate: () => void
}

const RegenerateButton = ({ isRegenerating, onRegenerate }: RegenerateButtonProps) => {
  return (
    <div className={Styles.regenerateButtonContainer}>
      <Button
        variant="tertiary"
        size="md"
        frontIcon={ArrowRefresh}
        color="var(--selected-fill)"
        isDisabled={isRegenerating}
        onPress={onRegenerate}
        aria-label="Regenerate topics"
      >
        {isRegenerating ? 'Regenerating...' : 'Regenerate'}
      </Button>
    </div>
  )
}

export const Topics = ({ name, title, icon, minValue = 0, description, isRegenerating, onRegenerate }: Props) => {
  const [showMore, setShowMore] = useState(false)
  const { trackTopicAdded, trackTopicDeleted } = useOnboardingTracking()
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()

  const [field, , helpers] = useField<string[]>(name)
  const hasTopics = field.value.length > 0

  const useMobileDrawer = isMobile

  const removeTopic = useCallback(
    (index: number) => {
      const topicText = field.value[index]
      const updated = [...field.value]
      updated.splice(index, 1)
      helpers.setValue(updated)
      trackTopicDeleted(topicText, name)
    },
    [field.value, helpers, name, trackTopicDeleted],
  )

  const updateTopic = useCallback(
    (index: number, newValue: string) => {
      const updated = [...field.value]
      updated[index] = newValue
      helpers.setValue(updated)
      if (newValue.length > 0) {
        trackTopicAdded(newValue, name)
      }
    },
    [field.value, helpers, name, trackTopicAdded],
  )

  const handleItemPress = useCallback(
    (index: number) => {
      drawersStore.openDrawer(TopicEditDrawer, {
        initialIndex: index,
        topics: field.value,
        minValue,
        onSave: (idx: number, newValue: string) => {
          if (newValue.length === 0 && field.value.length > minValue) {
            removeTopic(idx)
          } else {
            updateTopic(idx, newValue)
          }
        },
        onDelete: removeTopic,
      })
    },
    [drawersStore, field.value, minValue, removeTopic, updateTopic],
  )

  const handleAddTopic = useCallback(() => {
    drawersStore.openDrawer(TopicEditDrawer, {
      initialIndex: 0,
      topics: field.value,
      minValue,
      isNew: true,
      onAdd: (value: string) => {
        helpers.setValue([...field.value, value])
        trackTopicAdded(value, name)
      },
      onSave: () => {},
      onDelete: () => {},
    })
  }, [drawersStore, field.value, helpers, minValue, name, trackTopicAdded])

  return (
    <div className={Styles.topics} role="group" aria-label={title || startCase(name)}>
      <div className={Styles.topicsContainer}>
        <div className={Styles.header}>
          <div className={Styles.headerTop}>
            <div className={Styles.titleWithIcon}>
              {icon && <div className={Styles.icon}>{icon}</div>}
              <Heading level={3}>{title || 'Topics'}</Heading>
            </div>
            <div className={Styles.actions}>
              {onRegenerate && hasTopics && (
                <RegenerateButton isRegenerating={isRegenerating} onRegenerate={onRegenerate} />
              )}
              {useMobileDrawer ? (
                <div className={Styles.addTopicButtonContainer}>
                  <Button variant="tertiary" size="sm" frontIcon={Plus} onPress={handleAddTopic} aria-label="Add topic">
                    Add Topic
                  </Button>
                </div>
              ) : (
                <AddTopicButton name={name} onPress={() => setShowMore(true)} size="sm" />
              )}
            </div>
          </div>
          {description && (
            <Text variant="secondary" color="var(--dark-60)">
              {description}
            </Text>
          )}
        </div>

        <FormikArrayTextAreaInput
          name={name}
          minValue={minValue}
          showMore={showMore}
          onShowMore={() => setShowMore(true)}
          onShowLess={() => setShowMore(false)}
          onRemove={(text, name) => {
            if (text.length > 0) {
              trackTopicDeleted(text, name)
            }
          }}
          onAdd={(text, name) => {
            if (text.length > 0) {
              trackTopicAdded(text, name)
            }
          }}
          size="xl"
          disabled={isRegenerating}
          initialVisibleCount={useMobileDrawer ? 3 : 5}
          onItemPress={useMobileDrawer ? (_index: number, _value: string) => handleItemPress(_index) : undefined}
        />
      </div>
    </div>
  )
}
